import { PointLight, type Scene, type Vector3 } from 'three';

/** Świecąca dekoracja na mapie (świeca, latarnia, pochodnia). */
export interface LightSpot {
  pos: Vector3;
  color: string;
  intensity: number;
}

const FLASHES = 4;
const TORCHES = 4;

interface Flash {
  light: PointLight;
  t: number;
  duration: number;
  peak: number;
}

/**
 * Dynamiczne światła o stałej liczbie (shadery kompilują się raz, nic nie przycina):
 *  - błyski: wybuch, czar, krytyk, skrzynia na chwilę oświetlają teren i postacie,
 *  - ogniki: kilka najbliższych świec i latarni migocze ciepłym światłem.
 */
export class DynamicLights {
  private readonly flashes: Flash[] = [];
  private readonly torches: PointLight[] = [];
  private next = 0;
  private time = 0;

  constructor(scene: Scene) {
    for (let i = 0; i < FLASHES; i++) {
      const light = new PointLight('#ffffff', 0, 9, 2);
      scene.add(light);
      this.flashes.push({ light, t: 1, duration: 1, peak: 0 });
    }
    for (let i = 0; i < TORCHES; i++) {
      const light = new PointLight('#ffb060', 0, 7, 2);
      scene.add(light);
      this.torches.push(light);
    }
  }

  /** Krótki błysk światła w punkcie (np. wybuch: duży i pomarańczowy, krytyk: mały i złoty). */
  flash(at: Vector3, color: string, intensity: number, distance: number, duration: number): void {
    // Najpierw wolne światło, inaczej to, które najdłużej świeci.
    let f = this.flashes.find((x) => x.t >= x.duration);
    if (!f) {
      f = this.flashes[this.next]!;
      this.next = (this.next + 1) % FLASHES;
    }
    f.light.position.copy(at);
    f.light.color.set(color);
    f.light.distance = distance;
    f.t = 0;
    f.duration = duration;
    f.peak = intensity;
  }

  update(dt: number, center: Vector3, spots: readonly LightSpot[]): void {
    this.time += dt;
    for (const f of this.flashes) {
      if (f.t >= f.duration) {
        f.light.intensity = 0;
        continue;
      }
      f.t += dt;
      // Szybki rozbłysk i łagodne wygaszanie.
      const k = f.t / f.duration;
      f.light.intensity = f.peak * (k < 0.12 ? k / 0.12 : (1 - k) ** 2);
    }
    // Ogniki: najbliższe świecące dekoracje dostają światło (reszta i tak jest poza ekranem albo za mgłą).
    const nearest = [...spots]
      .map((s) => ({ s, d: s.pos.distanceToSquared(center) }))
      .filter((x) => x.d < 26 * 26)
      .sort((a, b) => a.d - b.d)
      .slice(0, TORCHES);
    this.torches.forEach((light, i) => {
      const spot = nearest[i]?.s;
      if (!spot) {
        light.intensity = 0;
        return;
      }
      light.position.copy(spot.pos);
      light.color.set(spot.color);
      // Migotanie płomienia: dwie nałożone fale + drobny szum.
      const phase = spot.pos.x * 3.1 + spot.pos.z * 1.7;
      const flicker = 0.82 + 0.1 * Math.sin(this.time * 9 + phase) + 0.06 * Math.sin(this.time * 23 + phase * 2) + 0.04 * Math.random();
      light.intensity = spot.intensity * flicker;
    });
  }

  /** Do debugowania: ile świateł świeci. */
  activeCount(): number {
    return [...this.flashes.map((f) => f.light), ...this.torches].filter((l) => l.intensity > 0).length;
  }
}
